/**
 * Anti-replay sliding window (SRTP-style, RFC 3711 §3.3.2).
 *
 * Tracks the highest authenticated frame counter plus a 64-frame bitmask of
 * recently seen counters. A malicious relay replaying recorded frames gets
 * them rejected here even though they decrypt correctly.
 *
 * Call accept(counter) only AFTER secretbox authentication succeeds —
 * otherwise forged counters could poison the window (DoS on legit frames).
 * Call reset() on every new key exchange.
 *
 * Same algorithm is ported to iOS (NativeTunnelClient.swift) and Android
 * (NativeTunnelClient.kt) — keep all three in sync.
 */
const WINDOW = 64n;
const MASK64 = (1n << 64n) - 1n;

export class ReplayWindow {
  constructor() {
    this.reset();
  }

  reset() {
    this._maxSeq = -1;  // highest accepted counter, -1 = none yet
    this._mask = 0n;    // bit i = (maxSeq - i) was seen
  }

  /** Returns true if the counter is new (frame should be played). */
  accept(seq) {
    seq = seq >>> 0; // counters are uint32; (b << 24) parsing may yield negatives

    if (this._maxSeq < 0) {
      this._maxSeq = seq;
      this._mask = 1n;
      return true;
    }

    if (seq > this._maxSeq) {
      const shift = BigInt(seq - this._maxSeq);
      this._mask = shift >= WINDOW ? 1n : ((this._mask << shift) | 1n) & MASK64;
      this._maxSeq = seq;
      return true;
    }

    const diff = BigInt(this._maxSeq - seq);
    if (diff >= WINDOW) return false; // too old to track — reject

    const bit = 1n << diff;
    if (this._mask & bit) return false; // replay

    this._mask |= bit;
    return true;
  }
}
